import {useTranslation} from '@/translations/i18n.tsx'
import {Spinner} from '@components/ui/spinner'
import {Header} from '@components/commons/header'
import {Container} from '@components/ui/container'
import {useDeleteFreelancerArea} from '@features/settings/queries/useDeleteFreelancerArea'
import {useChangeFreelancerAddress} from '@features/settings/queries/useChangeFreelancerAddress'
import {Coordinates} from '@features/settings/types'
import {retrieveAddressDataFromComponents} from '@features/settings/utils'
import {useState} from 'react'
import {useMe} from '@features/user/queries/useMe'
import {routes} from '@utilities/constants/routes'
import {AreaItem} from '@features/settings/components/area-item'
import {SETTINGS_AREA_DASHED_BORDER_BACKGROUND} from '@features/settings/constants.ts'
import {PlusIcon} from '@components/ui/icon'
import {AddAreasModal} from '@features/settings/components/add-areas-modal'
import {ButtonLink} from '@components/commons/button-link'
import {InputAddressAutocomplete} from '@components/commons/input-address-autocomplete'
import {AddressAlert} from '@features/settings/components/address-alert'

export const MarketsConfiguration = () => {
    const {t} = useTranslation()
    const userMeQuery = useMe()
    const user = userMeQuery.data
    const [deletingAreaId, setDeletingAreaId] = useState<number | null>(null)
    const [selectedAddress, setSelectedAddress] = useState<string | null>(null)

    const userLatitude = user?.latitude
    const userLongitude = user?.longitude
    const isCompleted = !!selectedAddress && !!userLatitude && !!userLongitude
    const areas = user?.photographerGeos ?? []
    const city = areas.find(area => area.isMain)?.geo.geo
    const countries = [...new Set(areas.map(geo => geo.geo && geo.geo.country))]

    const changeFreelancerAddressMutation = useChangeFreelancerAddress({
        userId: user?.id
    })

    const deleteFreelancerAreaMutation = useDeleteFreelancerArea({
        onSettled: () => setDeletingAreaId(null)
    })

    const handlerDeleteArea = (areaId: number) => {
        setDeletingAreaId(areaId)
        user?.id && deleteFreelancerAreaMutation.mutate({areaId, userId: user.id})
    }

    const onSelectAddressAutocomplete = (fullAddressValue: google.maps.GeocoderResult, latLng: Coordinates) => {
        const {country} = retrieveAddressDataFromComponents(fullAddressValue.address_components)
        setSelectedAddress(fullAddressValue.formatted_address)
        changeFreelancerAddressMutation.mutate({
            ...latLng,
            address: fullAddressValue.formatted_address,
            country
        })
    }

    if (userMeQuery.isPending) {
        return (
            <div className="flex justify-center items-center h-full w-full">
                <Spinner size={40} />
            </div>
        )
    }

    return (
        <div className="grid grid-rows-[auto_minmax(0,1fr)] overflow-hidden h-full bg-white">
            <div>
                <Header isPrivate={false} />

                {!!user?.isInspector && !user?.address && <AddressAlert isMarketConfiguration />}
            </div>

            <div className="overflow-y-auto h-full grid grid-rows-[1fr_auto] grid-cols-1">
                <Container className="pt-14 pb-4 flex justify-center">
                    <div className="flex flex-col gap-14 w-full max-w-150 mx-auto">
                        <h1 className="text-display-sm text-neutral-900 font-medium">
                            {t('markets_configuration:title')}
                        </h1>

                        {changeFreelancerAddressMutation.isPending ? (
                            <div className="flex justify-center items-center w-full">
                                <Spinner size={40} />
                            </div>
                        ) : (
                            <div className="flex flex-col gap-6 w-full">
                                <div className="flex flex-col gap-4">
                                    <div className="flex flex-col gap-1.5">
                                        <h3 className="text-md text-neutral-900 font-medium">
                                            {t('markets_configuration:address_title')}
                                        </h3>
                                        <p className="text-sm text-neutral-700">
                                            {user?.isPaidViaUpwork
                                                ? t('markets_configuration:address_description_upwork')
                                                : t('markets_configuration:address_description')}
                                        </p>
                                    </div>
                                    {selectedAddress ? (
                                        <div className="flex flex-col gap-1.5 w-full rounded-xl border border-neutral-300 py-3 px-4">
                                            <h3 className="text-neutral-900 font-medium">{city}</h3>
                                            <p className="text-neutral-700">{selectedAddress}</p>
                                        </div>
                                    ) : (
                                        <InputAddressAutocomplete
                                            staticView
                                            onChange={onSelectAddressAutocomplete}
                                            predictionTypes={['address']}
                                            disabled={changeFreelancerAddressMutation.isPending}
                                            placeholder={t('markets_configuration:address_placeholder')}
                                            className="p-0 [&>div]:p-0"
                                        />
                                    )}
                                </div>

                                {isCompleted && (
                                    <div className="flex flex-col gap-4 [&_h3]:text-md [&_h3]:text-neutral-900 [&_h3]:font-medium [&_p]:text-sm [&_p]:text-neutral-700">
                                        <div className="flex flex-col gap-2">
                                            <h3>{t('markets_configuration:areas_title')}</h3>
                                            <p>{t('markets_configuration:areas_description')}</p>
                                        </div>
                                        <div className="flex flex-wrap gap-3">
                                            {areas.map(
                                                area =>
                                                    !area.isMain && (
                                                        <AreaItem
                                                            key={area.geoId}
                                                            label={area.geo.geo}
                                                            countryCode={
                                                                countries.length > 1 ? area.geo.country : undefined
                                                            }
                                                            removeSelectedArea={() => {
                                                                handlerDeleteArea(area.geoId)
                                                            }}
                                                            isLoading={
                                                                deletingAreaId == area.geoId &&
                                                                deleteFreelancerAreaMutation.isPending
                                                            }
                                                        />
                                                    )
                                            )}
                                            <AddAreasModal>
                                                <AreaItem
                                                    className="cursor-pointer border-none! text-neutral-600"
                                                    style={{backgroundImage: SETTINGS_AREA_DASHED_BORDER_BACKGROUND}}
                                                >
                                                    <p>{t('settings:areas:add_areas')}</p>
                                                    <PlusIcon size={20} />
                                                </AreaItem>
                                            </AddAreasModal>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </Container>

                <div className="border-t border-neutral-200 p-4 flex items-center justify-center max-md:h-auto max-md:p-4">
                    <div className="flex justify-end items-center w-full max-w-150 mx-auto">
                        <ButtonLink to={routes.HOME.handle.fullPath} disabled={!user?.lastMarketsUpdatedAt}>
                            {t('commons:continue')}
                        </ButtonLink>
                    </div>
                </div>
            </div>
        </div>
    )
}

MarketsConfiguration.displayName = 'MarketsConfiguration'
